/**
 * fit3D.js — насколько отодвинуть камеру, чтобы объём занял окно целиком.
 *
 * Отдельным файлом и без единого импорта — по той же причине, что и
 * autoPreset3D: ошибка здесь не падает, а показывает картинку не того размера,
 * и заметна только глазом на реальном исследовании. Чистая функция проверяется
 * тестом на заранее известном ящике за миллисекунду.
 *
 * Задача. resetCamera вписывает объём по его габаритному шару, из-за чего в
 * ракурсе «сверху» или «спереди» объект занимает заметно меньше окна, чем
 * срез в соседнем окне, вписанный по прямоугольнику. Рядом, в раскладке из
 * шести окон, разница читается как «объёмные окна мельче остальных».
 *
 * Решение. Спроецировать восемь углов габаритного ящика на экранные оси
 * ТЕКУЩЕЙ камеры и взять максимум по каждой. Дальше — обычная арифметика
 * вписывания: по высоте либо по ширине, смотря что жёстче.
 */

/** Векторное произведение. */
function cross(a, b) {
  return [
    a[1] * b[2] - a[2] * b[1],
    a[2] * b[0] - a[0] * b[2],
    a[0] * b[1] - a[1] * b[0],
  ];
}

function norm(v) {
  const l = Math.hypot(v[0], v[1], v[2]);
  return l ? [v[0] / l, v[1] / l, v[2] / l] : null;
}

/**
 * @param {object} p
 * @param {number[]} p.bounds [xmin,xmax,ymin,ymax,zmin,zmax] в мировых координатах
 * @param {number[]} p.focalPoint точка, вокруг которой вращается камера
 * @param {number[]} p.position где камера стоит
 * @param {number[]} p.viewUp куда у камеры «вверх»
 * @param {number} [p.aspect=1] ширина окна / высота окна
 * @param {number} [p.fill=0.92] какую долю окна занять по стеснённой оси
 * @returns {{halfUp:number, halfRight:number, need:number, dir:number[]}|null}
 *   need — полувысота, которую камера обязана вместить (это и есть
 *   parallelScale для параллельной проекции); null, если данных не хватает.
 */
export function computeFit3D({ bounds, focalPoint, position, viewUp, aspect = 1, fill = 0.92 }) {
  if (!Array.isArray(bounds) || bounds.length !== 6) return null;
  if (!bounds.every(Number.isFinite) || bounds[1] < bounds[0]) return null;
  if (!Array.isArray(focalPoint) || !Array.isArray(position) || !Array.isArray(viewUp)) return null;

  const dir = norm([
    position[0] - focalPoint[0],
    position[1] - focalPoint[1],
    position[2] - focalPoint[2],
  ]);
  if (!dir) return null;

  // Экранная горизонталь. Если viewUp сонаправлен со взглядом, произведение
  // вырождается — считать нечего, и врать округлением тут нельзя.
  const right = norm(cross(viewUp, dir));
  if (!right) return null;
  // Экранная вертикаль — ортогонализованный viewUp: сам по себе он не обязан
  // быть строго перпендикулярен направлению взгляда.
  const up = cross(dir, right);

  let halfUp = 0, halfRight = 0;
  for (const x of [bounds[0], bounds[1]]) {
    for (const y of [bounds[2], bounds[3]]) {
      for (const z of [bounds[4], bounds[5]]) {
        const v = [x - focalPoint[0], y - focalPoint[1], z - focalPoint[2]];
        halfUp = Math.max(halfUp, Math.abs(v[0] * up[0] + v[1] * up[1] + v[2] * up[2]));
        halfRight = Math.max(halfRight, Math.abs(v[0] * right[0] + v[1] * right[1] + v[2] * right[2]));
      }
    }
  }
  if (!(halfUp > 0) && !(halfRight > 0)) return null;

  const a = Number.isFinite(aspect) && aspect > 0.05 ? aspect : 1;
  const f = Number.isFinite(fill) && fill > 0.1 ? fill : 0.92;
  const need = Math.max(halfUp, halfRight / a) / f;

  return { halfUp, halfRight, need, dir };
}

/** Расстояние до камеры при перспективе с полем зрения fovDeg по вертикали. */
export function distanceForFit(need, fovDeg = 30) {
  const t = Math.tan(((Number.isFinite(fovDeg) ? fovDeg : 30) * Math.PI) / 360);
  return t > 0 ? need / t : null;
}

export default { computeFit3D, distanceForFit };
